const isAbsoluteUrl = (value: string) => value.startsWith('http') || value.startsWith('data:');

const resolveResourceUrl = (path: string, publicUrl?: string) => {
  const base = (publicUrl ?? '').replace(/\/+$/, '');
  const key = path.replace(/^\/+/, '');
  return `${base}/${key}`;
};

/**
 * Resolve a user avatar the same way TestOps does: Kat One returns
 * `avatar` as a relative object key (`/<userUuid>.<ext>`) that lives on the
 * TestOps resource server (S3 bucket / CDN, `REACT_APP_RESOURCE_SERVER_URL`),
 * NOT on the Purchase Portal `publicUrl`. Absolute http/data URLs pass
 * through; an empty avatar yields the generic default image (legacy callers).
 */
export const getUserAvatar = (
  avatar?: string,
  resourceServerUrl: string | undefined = process.env.REACT_APP_RESOURCE_SERVER_URL,
) => {
  if (!avatar) {
    // return default avatar when user don't have avatar
    return process.env.REACT_APP_KATALON_DEFAULT_AVATAR_URL;
  }
  if (isAbsoluteUrl(avatar)) {
    // the avatar is full url, just return it
    return avatar;
  }
  if (!resourceServerUrl) {
    return `/${avatar.replace(/^\/+/, '')}`;
  }
  return resolveResourceUrl(avatar, resourceServerUrl);
};

/**
 * Resolve an account logo the same way TestOps does (`getAvatarSource` in
 * katalon-testops-g3-ui): an empty logo yields an empty string so the caller
 * can fall back to initials instead of a generic placeholder image; absolute
 * URLs (http/data:) are returned as-is; relative object keys (Kat One stores
 * `<accountUuid>.<ext>`) are resolved against the TestOps resource server
 * (S3 bucket / CDN, `REACT_APP_RESOURCE_SERVER_URL`, same value as G3's
 * `VITE_APP_RESOURCE_SERVER_URL`).
 */
export const getAccountAvatar = (
  logo?: string,
  resourceServerUrl: string | undefined = process.env.REACT_APP_RESOURCE_SERVER_URL,
): string => {
  if (!logo) {
    return '';
  }
  if (isAbsoluteUrl(logo)) {
    return logo;
  }
  if (!resourceServerUrl) {
    return `/${logo.replace(/^\/+/, '')}`;
  }
  return resolveResourceUrl(logo, resourceServerUrl);
};

/**
 * Initials fallback matching the TestOps Avatar default: first letter of each
 * of the first `maxInitials` whitespace-separated words, upper-cased.
 */
export const getInitials = (name?: string, maxInitials = 2) => (name ?? '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, maxInitials)
  .map(word => word[0])
  .join('')
  .toUpperCase();
